import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ArrowLeft, Home, Users, CheckCircle, AlertCircle, Download, Mail } from 'lucide-react';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface SchoolOrg {
  id: string;
  school_name: string;
  school_code: string;
  license_tier: number;
  licenses_used: number;
  subscription_status: string;
  subscription_end_date: string | null;
}

interface Student {
  user_id: string;
  display_name: string;
  date_of_birth: string;
  parent_guardian_email: string;
  parental_consent_given: boolean;
  created_at: string;
}

export default function SchoolAdmin() {
  const navigate = useNavigate();
  const [school, setSchool] = useState<SchoolOrg | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSchoolData();
  }, []);

  const loadSchoolData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please log in to access the school admin portal');
        navigate('/auth');
        return;
      }

      // Get school organization for this admin
      const { data: schoolData, error: schoolError } = await supabase
        .from('school_organizations')
        .select('*')
        .eq('contact_email', user.email)
        .single();

      if (schoolError || !schoolData) {
        toast.error('No school found for your account. Please contact support@therappi.app');
        return;
      }

      setSchool(schoolData);

      // Get students for this school
      const { data: studentsData, error: studentsError } = await supabase
        .from('profiles')
        .select('user_id, display_name, date_of_birth, parent_guardian_email, parental_consent_given, created_at')
        .eq('school_organization_id', schoolData.id)
        .eq('is_minor', true)
        .order('created_at', { ascending: false });

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
      } else {
        setStudents(studentsData || []);
      }
    } catch (error) {
      console.error('Error loading school data:', error);
      toast.error('Failed to load school data');
    } finally {
      setIsLoading(false);
    }
  };

  const exportStudentList = () => {
    if (!students.length) {
      toast.error('No students to export');
      return;
    }

    const csv = [
      ['Name', 'Date of Birth', 'Parent Email', 'Consent Status', 'Registered Date'],
      ...students.map(s => [
        s.display_name,
        s.date_of_birth,
        s.parent_guardian_email,
        s.parental_consent_given ? 'Confirmed' : 'Pending',
        new Date(s.created_at).toLocaleDateString(),
      ]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${school?.school_name}-students-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Student list exported');
  };

  const sendBulkEmail = () => {
    if (!students.length) {
      toast.error('No students to email');
      return;
    }

    const emails = students.map(s => s.parent_guardian_email).filter(Boolean).join(',');
    window.open(`mailto:${emails}?subject=${encodeURIComponent(`Update from ${school?.school_name}`)}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading school data...</p>
        </div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              No school found for your account. Please contact support@therappi.app
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const usagePercentage = (school.licenses_used / school.license_tier) * 100;
  const consentedStudents = students.filter(s => s.parental_consent_given).length;
  const pendingConsent = students.length - consentedStudents;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <img 
              src={therappiLogo} 
              alt="Therappi<sup className='text-[0.6em]'>®</sup> Logo" 
              className="w-8 h-8 object-contain"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
            <h1 className="text-xl font-semibold text-foreground">School Admin Portal</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* School Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{school.school_name}</CardTitle>
                <CardDescription>School Code: <span className="font-mono font-bold">{school.school_code}</span></CardDescription>
              </div>
              <Badge variant={school.subscription_status === 'active' ? 'default' : 'destructive'}>
                {school.subscription_status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* License Usage */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  License Usage
                </span>
                <span className="font-semibold">
                  {school.licenses_used} / {school.license_tier} students
                </span>
              </div>
              <Progress value={usagePercentage} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {school.license_tier - school.licenses_used} licenses remaining
              </p>
            </div>

            {/* Statistics Grid */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold">{consentedStudents}</p>
                      <p className="text-sm text-muted-foreground">Consented</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-8 w-8 text-yellow-500" />
                    <div>
                      <p className="text-2xl font-bold">{pendingConsent}</p>
                      <p className="text-sm text-muted-foreground">Pending Consent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-primary" />
                    <div>
                      <p className="text-2xl font-bold">{students.length}</p>
                      <p className="text-sm text-muted-foreground">Total Students</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={exportStudentList} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export List
              </Button>
              <Button onClick={sendBulkEmail} variant="outline">
                <Mail className="h-4 w-4 mr-2" />
                Email Parents
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Student List */}
        <Card>
          <CardHeader>
            <CardTitle>Registered Students</CardTitle>
            <CardDescription>
              Students who have registered using your school code
            </CardDescription>
          </CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No students registered yet</p>
                <p className="text-sm mt-2">Share your school code: <span className="font-mono font-bold">{school.school_code}</span></p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Date of Birth</TableHead>
                    <TableHead>Parent Email</TableHead>
                    <TableHead>Consent Status</TableHead>
                    <TableHead>Registered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.user_id}>
                      <TableCell className="font-medium">{student.display_name}</TableCell>
                      <TableCell>{new Date(student.date_of_birth).toLocaleDateString()}</TableCell>
                      <TableCell className="font-mono text-sm">{student.parent_guardian_email}</TableCell>
                      <TableCell>
                        {student.parental_consent_given ? (
                          <Badge variant="default">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Confirmed
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{new Date(student.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Contact Support */}
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              For questions about your school license, adding more students, or technical support:
            </p>
            <Button 
              onClick={() => window.open('mailto:support@therappi.app?subject=School License Support', '_blank')}
              variant="outline"
            >
              <Mail className="h-4 w-4 mr-2" />
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
